import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';
import ThemeToggle from '../../components/ThemeToggle';
import { Button } from '../../components/ui/button';
import {
    LayoutDashboard,
    FileText,
    AlertTriangle,
    Users,
    RefreshCw,
    Trash2,
    ClipboardList,
    LogOut,
    ShieldCheck,
    Menu,
    X,
    Award,
    Ticket,
    MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const AdminLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
        navigate('/');
    };

    const navItems = [
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/reviews', label: 'Reviews', icon: FileText },
        { path: '/admin/moderation', label: 'Moderation Queue', icon: AlertTriangle },
        { path: '/admin/verifications', label: 'Verifications', icon: Award },
        { path: '/admin/users', label: 'Users', icon: Users },
        { path: '/admin/invite-codes', label: 'Invite Codes', icon: Ticket },
        { path: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
        { path: '/admin/ownership', label: 'Ownership Resets', icon: RefreshCw },
        { path: '/admin/deletions', label: 'Deletion Requests', icon: Trash2 },
        { path: '/admin/audit', label: 'Audit Log', icon: ClipboardList },
    ];

    const isActive = (path) => {
        if (path === '/admin') return location.pathname === '/admin';
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-card border-r border-border">
                {/* Logo */}
                <div className="p-4 border-b border-border">
                    <Link to="/admin" className="flex items-center gap-2">
                        <Logo variant="icon" size="small" />
                        <div>
                            <span className="font-heading font-bold text-foreground">ClientCheck</span>
                            <span className="text-xs text-primary ml-1">Admin</span>
                        </div>
                    </Link>
                </div>

                {/* Admin Info */}
                <div className="p-4 border-b border-border bg-accent/30">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary/10">
                            <ShieldCheck className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{user?.company_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {navItems.map(({ path, label, icon: Icon }) => (
                        <Link
                            key={path}
                            to={path}
                            className={`
                                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                                ${isActive(path) 
                                    ? 'bg-primary text-primary-foreground' 
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                }
                            `}
                        >
                            <Icon className="h-5 w-5" />
                            {label}
                        </Link>
                    ))}
                </nav>

                {/* Footer */}
                <div className="p-4 border-t border-border space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Theme</span>
                        <ThemeToggle />
                    </div>
                    <Button 
                        variant="outline" 
                        className="w-full justify-start" 
                        onClick={handleLogout}
                    >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                    </Button>
                </div>
            </aside>

            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
                <div className="flex items-center justify-between p-4">
                    <Link to="/admin" className="flex items-center gap-2">
                        <Logo variant="icon" size="small" />
                        <span className="font-heading font-bold text-foreground">Admin</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <ThemeToggle />
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="bg-card border-b border-border p-4 space-y-1">
                        {navItems.map(({ path, label, icon: Icon }) => (
                            <Link
                                key={path}
                                to={path}
                                onClick={() => setMobileMenuOpen(false)}
                                className={`
                                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                                    ${isActive(path) 
                                        ? 'bg-primary text-primary-foreground' 
                                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                    }
                                `}
                            >
                                <Icon className="h-5 w-5" />
                                {label}
                            </Link>
                        ))}
                        <Button 
                            variant="outline" 
                            className="w-full justify-start mt-2" 
                            onClick={handleLogout}
                        >
                            <LogOut className="h-4 w-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                )}
            </div>

            {/* Main Content */}
            <main className="flex-1 lg:ml-64 min-h-screen">
                <div className="pt-16 lg:pt-0">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
